import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useI18n } from '../../context/I18nContext';
import { useTranslations } from '../../hooks/useTranslations';
import BaseFormModal from '../ui/BaseFormModal';
import DatePicker from '../ui/DatePicker';
import Input from '../ui/Input';
import { Payment } from '../../types';

interface MarkAsPaidFormData {
  paymentDate: Date | null;
  amountBaseCurrency: number;
}

interface MarkAsPaidModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (paymentDate: Date, amountBaseCurrency: number) => void;
  payment: Payment | null;
}

const MarkAsPaidModal: React.FC<MarkAsPaidModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  payment,
}) => {
  const { t } = useI18n();
  const { formatCurrency, formatDateLocale } = useTranslations();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<MarkAsPaidFormData>();

  useEffect(() => {
    if (isOpen && payment) {
      // Default to today's date
      const today = new Date();
      setValue('paymentDate', today);
      // Pre-fill amountBaseCurrency only if currency is NOT base currency
      // If currency is base currency, we don't need to ask (will use amount directly)
      const baseCurrency = 'EUR'; // User's base currency (will be configurable later)
      const paymentCurrency = payment.currency || 'EUR';
      if (paymentCurrency !== baseCurrency) {
        // Only pre-fill if currency is different from base
        const baseAmount = payment.amountBaseCurrency || 0;
        setValue('amountBaseCurrency', baseAmount);
      } else {
        // Currency is base currency - set to amount (won't be used but needed for form validation)
        setValue('amountBaseCurrency', payment.amount);
      }
    }
  }, [isOpen, payment, setValue]);

  const handleConfirm = (data: MarkAsPaidFormData) => {
    if (!data.paymentDate) return;
    
    const baseCurrency = 'EUR'; // User's base currency (will be configurable later)
    const paymentCurrency = payment?.currency || 'EUR';
    
    // If currency is base currency, we don't need amountBaseCurrency (will use amount in DB)
    // Otherwise, use the provided amountBaseCurrency
    const amountBaseCurrency = paymentCurrency === baseCurrency 
      ? (payment?.amount ?? 0)
      : (data.amountBaseCurrency || 0);
    
    // Close modal immediately for snappy UX
    onClose();
    
    // Confirm in background
    // Note: markAsPaid will handle the NULL logic based on currency
    Promise.resolve(onConfirm(data.paymentDate, amountBaseCurrency)).catch(error => {
      console.error('Error marking as paid:', error);
    });
  };

  return (
    <BaseFormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('payments.markAsPaid')}
      size="md"
      isLoading={false}
      mode="update"
      onValidate={handleSubmit(handleConfirm)}
    >
      {payment && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">{t('payments.amount')}:</span>
              <span className="text-sm font-semibold text-gray-900">
                {formatCurrency(payment.amount, payment.currency || 'EUR')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">{t('payments.dueDate')}:</span>
              <span className="text-sm text-gray-900">
                {formatDateLocale(payment.dueDate)}
              </span>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(handleConfirm)} noValidate className="space-y-4">
        <DatePicker
          label={t('payments.paymentDate')}
          value={watch('paymentDate')}
          onChange={(date) => setValue('paymentDate', date)}
          allowClear={false}
          maxDate={new Date()}
          error={errors.paymentDate?.message}
        />
        
        {/* Amount in base currency - only show if currency is NOT base currency */}
        {payment && (payment.currency || 'EUR') !== 'EUR' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <Input
              label={t('payments.amountReceivedBaseCurrency', { currency: 'EUR' })}
              type="number"
              step="0.01"
              min="0"
              {...register('amountBaseCurrency', { 
                required: t('payments.amountBaseCurrencyRequired') as any,
                min: { value: 0.01, message: t('payments.amountMin') as any }
              })}
              error={errors.amountBaseCurrency?.message}
              placeholder="0.00"
            />
            <p className="text-xs text-blue-700 mt-2">
              {t('payments.amountReceivedBaseCurrencyHelp')}
            </p>
          </div>
        )}
        
        {/* Hidden field for base currency payments (needed for form validation) */}
        {payment && (payment.currency || 'EUR') === 'EUR' && (
          <input
            type="hidden"
            {...register('amountBaseCurrency', { 
              value: payment.amount // Will be ignored (set to NULL in DB)
            })}
          />
        )}
      </form>
    </BaseFormModal>
  );
};

export default MarkAsPaidModal;

